/**
 * Cache Settings Tab Component
 *
 * Tab component for managing cache configuration settings
 * within the System Settings page.
 *
 * Uses React Query via useCacheSettings hook for data fetching
 * and mutation with automatic caching.
 */

import { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Dropdown,
  Option,
  Spinner,
  Text,
  shorthands,
} from '@fluentui/react-components';
import type { OptionOnSelectData, SelectionEvents } from '@fluentui/react-components';
import {
  Database20Regular,
  PeopleTeam20Regular,
  DocumentMultiple20Regular,
  ShieldLock20Regular,
  Desktop20Regular,
} from '@fluentui/react-icons';
import { Alert } from '../common/Alert';
import { RoundedCard } from '../common/RoundedCard';
import { useCacheSettings } from '../../hooks/useCacheSettings';
import type { CacheSettings } from '../../types/cacheSettings';
import { RESOURCE_TYPE_LABELS, TTL_SETTINGS_GROUPS } from '../../types/cacheSettings';

// Preset TTL options in minutes
const TTL_OPTIONS = [1, 2, 3, 5, 10, 15, 30, 60] as const;

// Preset max size options (number of items)
const MAX_SIZE_OPTIONS = [100, 250, 500, 1000, 2000, 5000, 10000] as const;

// Icons for each settings group
const GROUP_ICONS: Record<string, React.ReactNode> = {
  workspace: <PeopleTeam20Regular />,
  documents: <DocumentMultiple20Regular />,
  security: <ShieldLock20Regular />,
};

// Contextual descriptions for TTL values
const getTtlDescription = (minutes: number): string => {
  if (minutes <= 2) return 'Very fresh, frequent API calls';
  if (minutes <= 5) return 'Fresh data with moderate caching';
  if (minutes <= 15) return 'Balanced refresh rate';
  if (minutes <= 30) return 'Less frequent updates';
  return 'Long-lived cache, fewer API calls';
};

// Format milliseconds to readable string
const formatMilliseconds = (ms: number): string => {
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  const hours = minutes / 60;
  return `${hours} hour${hours !== 1 ? 's' : ''}`;
};

// Read React Query settings from environment variables
const getReactQuerySettings = () => ({
  staleTime: parseInt(import.meta.env.VITE_CACHE_STALE_TIME || '300000'),
  gcTime: parseInt(import.meta.env.VITE_CACHE_GC_TIME || '600000'),
  refetchOnFocus: import.meta.env.VITE_CACHE_REFETCH_ON_FOCUS === 'true',
});

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalL),
  },
  introDescription: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase300,
    ...shorthands.padding(0, 0, 0, '28px'),
  },
  card: {
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(0, 0, 0, tokens.spacingHorizontalS),
  },
  sectionIcon: {
    color: tokens.colorBrandForeground1,
    display: 'flex',
    alignItems: 'center',
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  sectionDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    ...shorthands.margin(0, 0, tokens.spacingVerticalS, 0),
    ...shorthands.padding(0, 0, 0, '28px'),
  },
  maxSizeField: {
    maxWidth: '280px',
    ...shorthands.padding(0, 0, 0, '28px'),
  },
  ttlGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    ...shorthands.gap(tokens.spacingVerticalXXL, tokens.spacingHorizontalXL),
    ...shorthands.padding(tokens.spacingVerticalM, 0, 0, '28px'),
  },
  ttlField: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  ttlFieldInner: {
    display: 'flex',
    flexDirection: 'column',
  },
  ttlLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    ...shorthands.margin(0, 0, tokens.spacingVerticalXS, 0),
  },
  ttlHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    ...shorthands.margin(tokens.spacingVerticalXS, 0, 0, 0),
    ...shorthands.padding(0, 0, 0, tokens.spacingHorizontalS),
  },
  readOnlyValue: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    display: 'inline-block',
  },
  buttonRow: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalM, 0, 0, 0),
  },
  hintText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    ...shorthands.margin(tokens.spacingVerticalXS, 0, 0, 0),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    ...shorthands.padding(tokens.spacingVerticalXXL),
  },
  maxSizeCard: {
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  ttlCard: {
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

export const CacheSettingsTab: React.FC = () => {
  const styles = useStyles();

  // Use React Query hook for data fetching and mutations
  const {
    settings,
    isLoading,
    error: fetchError,
    updateSettings,
    isUpdating,
    updateError,
  } = useCacheSettings();

  // Local UI state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state - TTL stored in minutes for UI, converted to/from seconds for API
  const [maxSize, setMaxSize] = useState(1000);
  const [ttlMinutes, setTtlMinutes] = useState<Record<string, number>>({
    teams: 10,
    sites: 10,
    recentDocuments: 3,
    siteDocuments: 5,
    folderChildren: 5,
    authMethods: 10,
    registrationCampaign: 10,
    conditionalAccessPolicies: 5,
    transportRules: 5,
  });

  // Track original values for dirty detection
  const originalValuesRef = useRef<CacheSettings | null>(null);

  // Convert seconds to minutes for display
  const secondsToMinutes = (seconds: number): number => Math.round(seconds / 60);

  // Convert minutes to seconds for API
  const minutesToSeconds = (minutes: number): number => minutes * 60;

  // Sync form state when settings are loaded from React Query
  useEffect(() => {
    if (settings) {
      setMaxSize(settings.maxSize);
      setTtlMinutes({
        teams: secondsToMinutes(settings.ttlSeconds.teams),
        sites: secondsToMinutes(settings.ttlSeconds.sites),
        recentDocuments: secondsToMinutes(settings.ttlSeconds.recentDocuments),
        siteDocuments: secondsToMinutes(settings.ttlSeconds.siteDocuments),
        folderChildren: secondsToMinutes(settings.ttlSeconds.folderChildren),
        authMethods: secondsToMinutes(settings.ttlSeconds.authMethods),
        registrationCampaign: secondsToMinutes(settings.ttlSeconds.registrationCampaign),
        conditionalAccessPolicies: secondsToMinutes(settings.ttlSeconds.conditionalAccessPolicies),
        transportRules: secondsToMinutes(settings.ttlSeconds.transportRules),
      });
      originalValuesRef.current = settings;
    }
  }, [settings]);

  // Check if form has unsaved changes (compare UI minutes to original seconds)
  const isDirty = (): boolean => {
    if (!originalValuesRef.current) return false;

    if (maxSize !== originalValuesRef.current.maxSize) return true;

    for (const group of TTL_SETTINGS_GROUPS) {
      for (const resourceType of group.resourceTypes) {
        const originalMinutes = secondsToMinutes(originalValuesRef.current.ttlSeconds[resourceType]);
        if (ttlMinutes[resourceType] !== originalMinutes) {
          return true;
        }
      }
    }

    return false;
  };

  const handleMaxSizeChange = (_ev: SelectionEvents, data: OptionOnSelectData) => {
    if (data.optionValue) {
      setMaxSize(parseInt(data.optionValue, 10));
      setSuccessMessage(null);
    }
  };

  // Find the closest max size option for a given value
  const findClosestMaxSizeOption = (value: number): number => {
    return MAX_SIZE_OPTIONS.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
  };

  const handleTtlChange = (resourceType: string) => {
    return (_ev: SelectionEvents, data: OptionOnSelectData) => {
      if (data.optionValue) {
        setTtlMinutes((prev) => ({
          ...prev,
          [resourceType]: parseInt(data.optionValue as string, 10),
        }));
        setSuccessMessage(null);
      }
    };
  };

  // Find the closest TTL option for a given value (for mapping loaded values to options)
  const findClosestOption = (value: number): number => {
    return TTL_OPTIONS.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
  };

  const handleSave = async () => {
    setSuccessMessage(null);

    try {
      // Convert UI minutes to API seconds
      const updatedSettings = await updateSettings({
        maxSize,
        ttlSeconds: {
          teams: minutesToSeconds(ttlMinutes.teams),
          sites: minutesToSeconds(ttlMinutes.sites),
          recentDocuments: minutesToSeconds(ttlMinutes.recentDocuments),
          siteDocuments: minutesToSeconds(ttlMinutes.siteDocuments),
          folderChildren: minutesToSeconds(ttlMinutes.folderChildren),
          authMethods: minutesToSeconds(ttlMinutes.authMethods),
          registrationCampaign: minutesToSeconds(ttlMinutes.registrationCampaign),
          conditionalAccessPolicies: minutesToSeconds(ttlMinutes.conditionalAccessPolicies),
          transportRules: minutesToSeconds(ttlMinutes.transportRules),
        },
      });

      // Update original values to reflect saved state
      originalValuesRef.current = updatedSettings;
      setSuccessMessage('Cache settings saved successfully');
    } catch {
      // Error is handled by React Query and exposed via updateError
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner label="Loading cache settings..." />
      </div>
    );
  }

  // Derive error message from React Query errors
  const errorMessage = fetchError?.message || updateError?.message || null;

  if (fetchError && !originalValuesRef.current) {
    return (
      <Alert intent="error" title="Error loading settings">
        {fetchError.message}
      </Alert>
    );
  }

  return (
    <div className={styles.container}>
      {successMessage && (
        <Alert intent="success" title="Success">
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert intent="error" title="Error">
          {errorMessage}
        </Alert>
      )}

      {/* Cache Size Section with Introduction */}
      <RoundedCard className={styles.maxSizeCard}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>
              <Database20Regular />
            </span>
            <Text className={styles.sectionTitle}>Cache Size</Text>
          </div>
          <Text className={styles.introDescription}>
            These settings control how long the backend caches responses from Microsoft Graph API
            before making fresh requests. Lower TTL values provide more up-to-date data but increase
            API calls and may hit rate limits. Higher TTL values reduce API calls but data may be stale.
            Changes apply immediately to new requests.
          </Text>
          <div className={styles.maxSizeField}>
            <div className={styles.ttlFieldInner}>
              <Text className={styles.ttlLabel}>Max Size</Text>
              <Dropdown
                value={`${findClosestMaxSizeOption(maxSize).toLocaleString()} items`}
                selectedOptions={[String(findClosestMaxSizeOption(maxSize))]}
                onOptionSelect={handleMaxSizeChange}
                aria-label="Max Size"
              >
                {MAX_SIZE_OPTIONS.map((size) => (
                  <Option key={size} value={String(size)} text={`${size.toLocaleString()} items`}>
                    {`${size.toLocaleString()} items`}
                  </Option>
                ))}
              </Dropdown>
              <Text className={styles.ttlHint}>
                Maximum number of items to keep in memory cache
              </Text>
            </div>
          </div>
        </div>
      </RoundedCard>

      {/* TTL Settings - One card per group */}
      {TTL_SETTINGS_GROUPS.map((group) => (
        <RoundedCard key={group.id} className={styles.ttlCard}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionIcon}>
                {GROUP_ICONS[group.id]}
              </span>
              <Text className={styles.sectionTitle}>{group.label}</Text>
            </div>
            <Text className={styles.sectionDescription}>{group.description}</Text>

            <div className={styles.ttlGrid}>
              {group.resourceTypes.map((resourceType) => {
                const currentValue = findClosestOption(ttlMinutes[resourceType]);
                return (
                  <div key={resourceType} className={styles.ttlField}>
                    <div className={styles.ttlFieldInner}>
                      <Text className={styles.ttlLabel}>
                        {RESOURCE_TYPE_LABELS[resourceType]}
                      </Text>
                      <Dropdown
                        value={`${currentValue} ${currentValue === 1 ? 'minute' : 'minutes'}`}
                        selectedOptions={[String(currentValue)]}
                        onOptionSelect={handleTtlChange(resourceType)}
                        aria-label={RESOURCE_TYPE_LABELS[resourceType]}
                      >
                        {TTL_OPTIONS.map((minutes) => {
                          const label = `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
                          return (
                            <Option key={minutes} value={String(minutes)} text={label}>
                              {label}
                            </Option>
                          );
                        })}
                      </Dropdown>
                      <Text className={styles.ttlHint}>
                        {getTtlDescription(currentValue)}
                      </Text>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </RoundedCard>
      ))}

      {/* Frontend Cache Settings (Read-only) */}
      <RoundedCard className={styles.ttlCard}>
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>
              <Desktop20Regular />
            </span>
            <Text className={styles.sectionTitle}>Frontend Cache (React Query)</Text>
          </div>
          <Text className={styles.sectionDescription}>
            Client-side caching configured via environment variables. These settings are read-only
            and require rebuilding the frontend to change.
          </Text>

          <div className={styles.ttlGrid}>
            <div className={styles.ttlField}>
              <div className={styles.ttlFieldInner}>
                <Text className={styles.ttlLabel}>Stale Time</Text>
                <Text className={styles.readOnlyValue}>
                  {formatMilliseconds(getReactQuerySettings().staleTime)}
                </Text>
                <Text className={styles.ttlHint}>
                  How long data is considered fresh before refetching
                </Text>
              </div>
            </div>

            <div className={styles.ttlField}>
              <div className={styles.ttlFieldInner}>
                <Text className={styles.ttlLabel}>Garbage Collection Time</Text>
                <Text className={styles.readOnlyValue}>
                  {formatMilliseconds(getReactQuerySettings().gcTime)}
                </Text>
                <Text className={styles.ttlHint}>
                  How long inactive data stays in cache before removal
                </Text>
              </div>
            </div>

            <div className={styles.ttlField}>
              <div className={styles.ttlFieldInner}>
                <Text className={styles.ttlLabel}>Refetch on Window Focus</Text>
                <Text className={styles.readOnlyValue}>
                  {getReactQuerySettings().refetchOnFocus ? 'Enabled' : 'Disabled'}
                </Text>
                <Text className={styles.ttlHint}>
                  Whether to refetch data when browser tab regains focus
                </Text>
              </div>
            </div>
          </div>
        </div>
      </RoundedCard>

      {/* Save Button */}
      <div className={styles.buttonRow}>
        <Button
          appearance="primary"
          onClick={handleSave}
          disabled={!isDirty() || isUpdating}
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default CacheSettingsTab;
